import { useEffect, useState, useRef } from "react";
import { Toast, ToastContainer, Button, Col, Container, Row } from "react-bootstrap"
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik'
import BForm from 'react-bootstrap/Form'
import * as Yup from 'yup'
import { getAuthHeader, REACT_APP_PS_PORTAL_API_URI } from "authHelper";
import apiFetch from "apiFetch";
import { useMsal } from "@azure/msal-react";
import _ from 'lodash'
import { LinXmartDialog } from 'components/LinXmartDialog'

function LinXmartToggleField() {
    const { values, touched, setFieldValue, setFieldTouched } = useFormikContext()
    const wasEnabled = useRef(values.isLinXmart)

    const handleChange = e => {
        const checked = e.target.checked
        setFieldValue('isLinXmart', checked)
        if (checked && !wasEnabled.current) {
            setFieldValue('linXmartKey', '', false)
            setFieldTouched('linXmartKey', true, false)
        }
        if (!checked) wasEnabled.current = false
    }

    return (
        <BForm.Group controlId="isLinXmart" className="mb-3">
            <BForm.Check
                type="checkbox"
                label="LinXmart Enabled"
                name="isLinXmart"
                checked={values.isLinXmart}
                onChange={handleChange}
                isInvalid={!!(values.isLinXmart === false && touched.isLinXmart === true)}
            />
            <ErrorMessage
                name="isLinXmart"
                component="div"
                className="invalid-feedback d-block"
            />
        </BForm.Group>
    )
}

const PHNSettings = () => {
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [showToastMsg, setShowToastMsg] = useState({ visible: false, msg: '' })
    const [showConfirmLinXmartChange, setShowConfirmLinXmartChange] = useState(false);
    const [showConfirmLinXmartDisable, setShowConfirmLinXmartDisable] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(null);
    const { instance, accounts } = useMsal()
    let [phn, setPHN] = useState({})
    const [refData, setRefData] = useState([])

    async function fetchData() {
        setIsRefreshing(true)

        const authHeader = await getAuthHeader(instance, accounts)
        const httpConfig = {
            headers: authHeader
        }

        const result = await apiFetch.get(`${REACT_APP_PS_PORTAL_API_URI}/phns/1`, httpConfig)
        const data = result.data
        const phnDetails = {
            id: data.id,
            name: _.get(data, 'name'),
            phnEmail: _.get(data, 'phnEmail'),
            webUrl: _.get(data, 'webUrl'),
            // TODO: isAcgEnabled: _.get(data, 'isAcgEnabled'),
            isLinXmart: _.get(data, 'isLinXmart'),
            linXmartKey: _.get(data, 'linXmartKey'),
            linXmartUpdated: new Date(_.get(data, 'linXmartUpdated')),
            linXmartDefinitionID: _.get(data, 'linXmartDefinitionID'),
            isAlertsEnabled: _.get(data, 'isAlertsEnabled'),
            isPromptsEnabled: _.get(data, 'isPromptsEnabled')
        }
        setPHN(phnDetails)

        const refResult = await apiFetch.get(`${REACT_APP_PS_PORTAL_API_URI}/phns/refdata`, httpConfig)
        setRefData(refResult.data)

        setIsRefreshing(false)
    }

    useEffect(() => {
        fetchData().catch(() => undefined)
    }, [])

    const validationSchema = Yup.object().shape({
        phnEmail: Yup.string()
            .nullable()
            .required('PHN Email is required')
            .email('PHN Email is not valid'),
        webUrl: Yup.string()
            .nullable()
            .required('PHN URL is required')
            .url('PHN URL is not valid'),
        isLinXmart: Yup.boolean(),
        linXmartKey: Yup.string()
            .nullable()
            .when("isLinXmart", {
                is: true,
                then: schema => schema
                    .required("HMAC Key is required")
                    .max(100, 'Key must be at most 100 characters')
                    //TODO - Need to adjust it to the appropriate Curtin recommendation in future
                    .min(3, 'Key must be at least 3 characters'),
                otherwise: schema => schema.notRequired()
            }),
        linXmartDefinitionID: Yup.number()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .typeError('Project Definiton is required')
            .nullable()
            .when("isLinXmart", {
                is: true,
                then: schema => schema.required("Project Definition must be selected"),
                otherwise: schema => schema.notRequired()
            })
    })

    const submitPHNSettings = async (values, helpers) => {
        const { setSubmitting, resetForm } = helpers;
        setIsRefreshing(true);
        const authHeader = await getAuthHeader(instance, accounts);
        let send = { ...values };

        if (values.linXmartKey === phn.linXmartKey) {
            send.linXmartKey = undefined;
        }

        await apiFetch.put(`${REACT_APP_PS_PORTAL_API_URI}/phns/settings`, send, { headers: authHeader });
        setSubmitting(false);
        resetForm({ values });
        setPHN(values);
        setShowToastMsg({ visible: true, msg: 'PHN Settings Saved successfully' });
        setIsRefreshing(false);
    };

    return (
        <>
            <ToastContainer className="p-3" position="bottom-center">
                <Toast onClose={() => setShowToastMsg({ visible: false, msg: '' })} show={showToastMsg.visible} delay={3000} autohide>
                    <Toast.Header>
                        <strong className="me-auto">Primary Sense</strong>
                    </Toast.Header>
                    <Toast.Body>{showToastMsg.msg}</Toast.Body>
                </Toast>
            </ToastContainer>
            {phn.id && <Formik
                enableReinitialize={true}
                initialValues={phn}
                validationSchema={validationSchema}
                onSubmit={async (values, helpers) => {
                    if (values.isLinXmart === phn.isLinXmart && values.linXmartKey === phn.linXmartKey) {
                        await submitPHNSettings(values, helpers);
                    }
                    if (!values.isLinXmart && phn.isLinXmart) { //isLinXmart disabled
                        setPendingSubmit({ values, helpers });
                        setShowConfirmLinXmartDisable(true);
                    }
                    if (values.isLinXmart && !phn.isLinXmart) { //isLinXmart enabled
                        setPendingSubmit({ values, helpers });
                        setShowConfirmLinXmartChange(true);
                    }
                    if (values.isLinXmart && phn.isLinXmart && values.linXmartKey !== phn.linXmartKey) { //isLinXmart not toggled, so key has changed
                        setPendingSubmit({ values, helpers });
                        setShowConfirmLinXmartChange(true);
                    }
                }}
            >
                {({ handleSubmit, errors, touched, isSubmitting, isValidating, dirty, resetForm, values }) => {
                    return (
                        <>
                            <Container>
                                <Form>
                                    <Row className="mb-3">
                                        <Col xs={4}>
                                            <BForm.Group>
                                                <BForm.Label>PHN Name</BForm.Label>
                                                <Field type="text" name="name" disabled={true} className="form-control" />
                                                <ErrorMessage name="name" component="div" className="invalid-feedback d-block" />
                                            </BForm.Group>
                                        </Col>
                                        <Col xs={4}>
                                            <BForm.Group>
                                                <BForm.Label>PHN Email</BForm.Label>
                                                <Field type="text" name="phnEmail" className="form-control" />
                                                <ErrorMessage name="phnEmail" component="div" className="invalid-feedback d-block" />
                                            </BForm.Group>
                                        </Col>
                                        <Col xs={4}>
                                            <BForm.Group>
                                                <BForm.Label>PHN URL</BForm.Label>
                                                <Field type="text" name="webUrl" className="form-control" />
                                                <ErrorMessage name="webUrl" component="div" className="invalid-feedback d-block" />
                                            </BForm.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col xs={4}>
                                        </Col>
                                        <Col xs={4}>
                                            <BForm.Group className="form-check">
                                                <BForm.Label>
                                                    <Field type="checkbox" name="isAlertsEnabled" className="form-check-input" />
                                                    Alerts Enabled
                                                </BForm.Label>
                                                <ErrorMessage name="isAlertsEnabled" component="div" className="invalid-feedback d-block" />
                                            </BForm.Group>
                                        </Col>
                                        <Col xs={4}>
                                            <BForm.Group className="form-check">
                                                <BForm.Label>
                                                    <Field type="checkbox" name="isPromptsEnabled" className="form-check-input" />
                                                    Prompts Enabled
                                                </BForm.Label>
                                                <ErrorMessage name="isPromptsEnabled" component="div" className="invalid-feedback d-block" />
                                            </BForm.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col> {/*className="mt-auto"*/}
                                            <LinXmartToggleField />
                                        </Col>
                                        <Col>
                                            <BForm.Group>
                                                {values.isLinXmart &&
                                                    <>
                                                        <p>LinXmart Key Updated</p>
                                                        <p>
                                                            {values.linXmartUpdated.toLocaleString('en-AU')}
                                                        </p>
                                                    </>
                                                }
                                            </BForm.Group>
                                        </Col>
                                        <Col xs={4}>
                                            <BForm.Group>
                                                {values.isLinXmart &&
                                                    <>
                                                        <BForm.Label>LinXmart Key</BForm.Label>
                                                        <Field type="text" name="linXmartKey" className="form-control" />
                                                        <ErrorMessage name="linXmartKey" component="div" className="invalid-feedback d-block" />
                                                    </>
                                                }
                                            </BForm.Group>
                                        </Col>
                                        <Col xs={4}>
                                            <BForm.Group>
                                                {values.isLinXmart &&
                                                    <>
                                                        <BForm.Label>LinXmart Project Definition</BForm.Label>
                                                        <Field name="linXmartDefinitionID" as="select" className="form-control form-select">
                                                            <option value=""></option>
                                                            {refData && refData.map(s => (
                                                                <option key={s.id} value={s.id}>{s.name}</option>
                                                            ))}
                                                        </Field>
                                                        <ErrorMessage name="linXmartDefinitionID" component="div" className="invalid-feedback d-block" />
                                                    </>
                                                }
                                            </BForm.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col>
                                            <Button variant="outline-primary" disabled={isSubmitting || !dirty} type="submit">
                                                Save
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Container>
                            <LinXmartDialog
                                show={showConfirmLinXmartDisable}
                                pendingSubmit={pendingSubmit}
                                onClose={() => {
                                    setShowConfirmLinXmartDisable(false)
                                    setPendingSubmit(null)
                                }}
                                submitPHNSettings={submitPHNSettings}
                                title="Confirm Disable"
                                body="Disabling LinXmart will result in previously collected data being permanently deleted from the database for all practices in this PHN, outside of hours."
                            />
                            <LinXmartDialog
                                show={showConfirmLinXmartChange}
                                pendingSubmit={pendingSubmit}
                                onClose={() => {
                                    setShowConfirmLinXmartChange(false)
                                    setPendingSubmit(null)
                                }}
                                submitPHNSettings={submitPHNSettings}
                                title="Confirm Change"
                                body="Changing the LinXmart Key will trigger re-extraction for all enabled practices within this PHN, outside of hours."
                            />
                        </>
                    )
                }}
            </Formik>}
        </>
    )
}

export default PHNSettings;